import React, { useState, useEffect } from 'react';
import { MemoryPlace } from '../../services/memoryService';
import { generateImage } from '../../services/imageService';
import './MemoryPalace.css';

interface PlaceImageGeneratorProps {
  place: MemoryPlace;
  isOpen: boolean;
  onClose: () => void;
  onImageGenerated: (imageUrl: string) => void;
}

const PlaceImageGenerator: React.FC<PlaceImageGeneratorProps> = ({
  place,
  isOpen,
  onClose,
  onImageGenerated
}) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate default prompt when component opens or place changes
  useEffect(() => {
    if (isOpen && place) {
      const defaultPrompt = `A detailed, artistic visualization of ${place.name}${place.description ? `. ${place.description}` : ''}`;
      setPrompt(defaultPrompt);
      setError(null);
    }
  }, [isOpen, place]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      const response = await generateImage(prompt.trim());

      if (response.success && response.data?.imageUrl) {
        onImageGenerated(response.data.imageUrl);
        onClose();
      } else {
        setError(response.message || response.error || 'Failed to generate image');
      }
    } catch (err) {
      console.error('Error generating image:', err);
      setError('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="memory-palace-form-overlay" onClick={onClose}>
      <div className="memory-palace-form" onClick={(e) => e.stopPropagation()}>
        <div className="memory-palace-form-header">
          <h2>Generate Image</h2>
          <button className="memory-palace-form-close" onClick={onClose}>×</button>
        </div>
        <div className="memory-palace-form-content">
          <div className="memory-palace-form-field">
            <label htmlFor="image-prompt">Prompt</label>
            <textarea
              id="image-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter a description for the image you want to generate..."
              rows={6}
              className="memory-palace-form-textarea"
              disabled={isGenerating}
            />
            <div className="memory-palace-form-hint">
              Press Ctrl+Enter (or Cmd+Enter on Mac) to generate
            </div>
          </div>

          {error && (
            <div className="memory-palace-form-error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <div className="memory-palace-form-actions">
            <button
              type="button"
              onClick={onClose}
              className="memory-palace-form-btn"
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              className="memory-palace-form-btn save"
              disabled={isGenerating || !prompt.trim()}
            >
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceImageGenerator;

